import type { MapCollectionRef } from "./types/map";

// ─── Map layers (ATL-106) ───────────────────────────────────────────────────
// Per-collection visibility on the explore map, shared by web and mobile so
// the multi-membership rule stays identical on both. The user's exclusion
// list is collection slugs; map payloads carry membership as indexes into
// their `collections` lookup.

/**
 * Resolve the user's hidden collection slugs against a payload's collection
 * lookup. Slugs missing from the lookup (removed collections, older payloads)
 * are ignored - unknown entries can never hide anything.
 */
export function hiddenCollectionIndexSet(
  lookup: MapCollectionRef[] | undefined,
  hiddenSlugs: Iterable<string>,
): Set<number> {
  const out = new Set<number>();
  if (!lookup || lookup.length === 0) return out;
  const idxBySlug = new Map<string, number>();
  lookup.forEach((c, i) => idxBySlug.set(c.slug, i));
  for (const slug of hiddenSlugs) {
    const idx = idxBySlug.get(slug);
    if (idx != null) out.add(idx);
  }
  return out;
}

/**
 * Multi-membership rule: an item hides only when ALL collections it belongs
 * to are hidden. Items in no collection (or from payloads without membership
 * data) are never hidden.
 */
export function isMapItemVisible(
  membership: number[] | undefined,
  hiddenIdx: ReadonlySet<number>,
): boolean {
  if (!membership || membership.length === 0 || hiddenIdx.size === 0) return true;
  for (const idx of membership) {
    if (!hiddenIdx.has(idx)) return true;
  }
  return false;
}
